import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Neighborhood CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Neighborhood points of interest
//
// Each POI card is a slot group with:
//   • one editor-visible field: `poi_name`
//   • four hidden fields (`_`-prefixed): walking / biking / driving / miles
//
// A `sourceBinding` on `neighborhood_pois` gives editors a POI-restricted
// whole-item picker — clicking a POI card opens the platform's collection
// item picker (rendered as a photo grid, because `neighborhood_pois` has
// `imageField: 'poi_featured_image'` registered in `@vibesites/schema` —
// that's a platform decision and can't be flipped to a name list from a
// theme). Picking a POI swaps all five fields in one shot, so the hidden
// distance fields stay coherent with the visible name.
//
// The individual field-picker (drilling into "Name" from the slot sidebar)
// still shows every text field on the property — the platform picker isn't
// filterable from a theme. The whole-item photo picker is the primary flow.
// ============================================================================

interface NeighborhoodPoiSlot {
  poi_name?: string | null;
  _poi_walking_minutes?: string | null;
  _poi_biking_minutes?: string | null;
  _poi_driving_minutes?: string | null;
  _poi_miles_from_property?: string | null;
}

// One inline literal per slot — the schema extractor follows one level of
// module-level `const` reference, and requires pure literals (no function
// calls, no computed keys). If you add/remove a POI slot, update the JSX
// in NeighborhoodSection and the slotGroups entry in contentSchema in
// lockstep.

const poi1Def: SlotGroupDefinition = {
  label: 'POI 1',
  description: 'A point of interest shown under the copy.',
  fields: {
    poi_name: {
      label: 'Name',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_name',
      suggestedFields: [
        'neighborhood_pois[0].poi_name',
        'neighborhood_pois[1].poi_name',
        'neighborhood_pois[2].poi_name',
        'neighborhood_pois[3].poi_name',
        'neighborhood_pois[4].poi_name',
        'neighborhood_pois[5].poi_name',
      ],
    },
    _poi_walking_minutes: {
      label: 'Walking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_walking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_walking_minutes',
        'neighborhood_pois[1].poi_walking_minutes',
        'neighborhood_pois[2].poi_walking_minutes',
        'neighborhood_pois[3].poi_walking_minutes',
        'neighborhood_pois[4].poi_walking_minutes',
        'neighborhood_pois[5].poi_walking_minutes',
      ],
    },
    _poi_biking_minutes: {
      label: 'Biking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_biking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_biking_minutes',
        'neighborhood_pois[1].poi_biking_minutes',
        'neighborhood_pois[2].poi_biking_minutes',
        'neighborhood_pois[3].poi_biking_minutes',
        'neighborhood_pois[4].poi_biking_minutes',
        'neighborhood_pois[5].poi_biking_minutes',
      ],
    },
    _poi_driving_minutes: {
      label: 'Driving Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_driving_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_driving_minutes',
        'neighborhood_pois[1].poi_driving_minutes',
        'neighborhood_pois[2].poi_driving_minutes',
        'neighborhood_pois[3].poi_driving_minutes',
        'neighborhood_pois[4].poi_driving_minutes',
        'neighborhood_pois[5].poi_driving_minutes',
      ],
    },
    _poi_miles_from_property: {
      label: 'Miles from Property',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_miles_from_property',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_miles_from_property',
        'neighborhood_pois[1].poi_miles_from_property',
        'neighborhood_pois[2].poi_miles_from_property',
        'neighborhood_pois[3].poi_miles_from_property',
        'neighborhood_pois[4].poi_miles_from_property',
        'neighborhood_pois[5].poi_miles_from_property',
      ],
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: {
      poi_name: 'poi_name',
      _poi_walking_minutes: 'poi_walking_minutes',
      _poi_biking_minutes: 'poi_biking_minutes',
      _poi_driving_minutes: 'poi_driving_minutes',
      _poi_miles_from_property: 'poi_miles_from_property',
    },
  },
};

const poi2Def: SlotGroupDefinition = {
  label: 'POI 2',
  description: 'A point of interest shown under the copy.',
  fields: {
    poi_name: {
      label: 'Name',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_name',
      suggestedFields: [
        'neighborhood_pois[0].poi_name',
        'neighborhood_pois[1].poi_name',
        'neighborhood_pois[2].poi_name',
        'neighborhood_pois[3].poi_name',
        'neighborhood_pois[4].poi_name',
        'neighborhood_pois[5].poi_name',
      ],
    },
    _poi_walking_minutes: {
      label: 'Walking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_walking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_walking_minutes',
        'neighborhood_pois[1].poi_walking_minutes',
        'neighborhood_pois[2].poi_walking_minutes',
        'neighborhood_pois[3].poi_walking_minutes',
        'neighborhood_pois[4].poi_walking_minutes',
        'neighborhood_pois[5].poi_walking_minutes',
      ],
    },
    _poi_biking_minutes: {
      label: 'Biking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_biking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_biking_minutes',
        'neighborhood_pois[1].poi_biking_minutes',
        'neighborhood_pois[2].poi_biking_minutes',
        'neighborhood_pois[3].poi_biking_minutes',
        'neighborhood_pois[4].poi_biking_minutes',
        'neighborhood_pois[5].poi_biking_minutes',
      ],
    },
    _poi_driving_minutes: {
      label: 'Driving Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_driving_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_driving_minutes',
        'neighborhood_pois[1].poi_driving_minutes',
        'neighborhood_pois[2].poi_driving_minutes',
        'neighborhood_pois[3].poi_driving_minutes',
        'neighborhood_pois[4].poi_driving_minutes',
        'neighborhood_pois[5].poi_driving_minutes',
      ],
    },
    _poi_miles_from_property: {
      label: 'Miles from Property',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_miles_from_property',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_miles_from_property',
        'neighborhood_pois[1].poi_miles_from_property',
        'neighborhood_pois[2].poi_miles_from_property',
        'neighborhood_pois[3].poi_miles_from_property',
        'neighborhood_pois[4].poi_miles_from_property',
        'neighborhood_pois[5].poi_miles_from_property',
      ],
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 1,
    fieldMappings: {
      poi_name: 'poi_name',
      _poi_walking_minutes: 'poi_walking_minutes',
      _poi_biking_minutes: 'poi_biking_minutes',
      _poi_driving_minutes: 'poi_driving_minutes',
      _poi_miles_from_property: 'poi_miles_from_property',
    },
  },
};

const poi3Def: SlotGroupDefinition = {
  label: 'POI 3',
  description: 'A point of interest shown under the copy.',
  fields: {
    poi_name: {
      label: 'Name',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_name',
      suggestedFields: [
        'neighborhood_pois[0].poi_name',
        'neighborhood_pois[1].poi_name',
        'neighborhood_pois[2].poi_name',
        'neighborhood_pois[3].poi_name',
        'neighborhood_pois[4].poi_name',
        'neighborhood_pois[5].poi_name',
      ],
    },
    _poi_walking_minutes: {
      label: 'Walking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_walking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_walking_minutes',
        'neighborhood_pois[1].poi_walking_minutes',
        'neighborhood_pois[2].poi_walking_minutes',
        'neighborhood_pois[3].poi_walking_minutes',
        'neighborhood_pois[4].poi_walking_minutes',
        'neighborhood_pois[5].poi_walking_minutes',
      ],
    },
    _poi_biking_minutes: {
      label: 'Biking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_biking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_biking_minutes',
        'neighborhood_pois[1].poi_biking_minutes',
        'neighborhood_pois[2].poi_biking_minutes',
        'neighborhood_pois[3].poi_biking_minutes',
        'neighborhood_pois[4].poi_biking_minutes',
        'neighborhood_pois[5].poi_biking_minutes',
      ],
    },
    _poi_driving_minutes: {
      label: 'Driving Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_driving_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_driving_minutes',
        'neighborhood_pois[1].poi_driving_minutes',
        'neighborhood_pois[2].poi_driving_minutes',
        'neighborhood_pois[3].poi_driving_minutes',
        'neighborhood_pois[4].poi_driving_minutes',
        'neighborhood_pois[5].poi_driving_minutes',
      ],
    },
    _poi_miles_from_property: {
      label: 'Miles from Property',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_miles_from_property',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_miles_from_property',
        'neighborhood_pois[1].poi_miles_from_property',
        'neighborhood_pois[2].poi_miles_from_property',
        'neighborhood_pois[3].poi_miles_from_property',
        'neighborhood_pois[4].poi_miles_from_property',
        'neighborhood_pois[5].poi_miles_from_property',
      ],
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 2,
    fieldMappings: {
      poi_name: 'poi_name',
      _poi_walking_minutes: 'poi_walking_minutes',
      _poi_biking_minutes: 'poi_biking_minutes',
      _poi_driving_minutes: 'poi_driving_minutes',
      _poi_miles_from_property: 'poi_miles_from_property',
    },
  },
};

const poi4Def: SlotGroupDefinition = {
  label: 'POI 4',
  description: 'A point of interest shown under the copy.',
  fields: {
    poi_name: {
      label: 'Name',
      type: 'text',
      defaultField: 'neighborhood_pois[3].poi_name',
      suggestedFields: [
        'neighborhood_pois[0].poi_name',
        'neighborhood_pois[1].poi_name',
        'neighborhood_pois[2].poi_name',
        'neighborhood_pois[3].poi_name',
        'neighborhood_pois[4].poi_name',
        'neighborhood_pois[5].poi_name',
      ],
    },
    _poi_walking_minutes: {
      label: 'Walking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[3].poi_walking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_walking_minutes',
        'neighborhood_pois[1].poi_walking_minutes',
        'neighborhood_pois[2].poi_walking_minutes',
        'neighborhood_pois[3].poi_walking_minutes',
        'neighborhood_pois[4].poi_walking_minutes',
        'neighborhood_pois[5].poi_walking_minutes',
      ],
    },
    _poi_biking_minutes: {
      label: 'Biking Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[3].poi_biking_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_biking_minutes',
        'neighborhood_pois[1].poi_biking_minutes',
        'neighborhood_pois[2].poi_biking_minutes',
        'neighborhood_pois[3].poi_biking_minutes',
        'neighborhood_pois[4].poi_biking_minutes',
        'neighborhood_pois[5].poi_biking_minutes',
      ],
    },
    _poi_driving_minutes: {
      label: 'Driving Minutes',
      type: 'text',
      defaultField: 'neighborhood_pois[3].poi_driving_minutes',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_driving_minutes',
        'neighborhood_pois[1].poi_driving_minutes',
        'neighborhood_pois[2].poi_driving_minutes',
        'neighborhood_pois[3].poi_driving_minutes',
        'neighborhood_pois[4].poi_driving_minutes',
        'neighborhood_pois[5].poi_driving_minutes',
      ],
    },
    _poi_miles_from_property: {
      label: 'Miles from Property',
      type: 'text',
      defaultField: 'neighborhood_pois[3].poi_miles_from_property',
      defaultValue: '',
      suggestedFields: [
        'neighborhood_pois[0].poi_miles_from_property',
        'neighborhood_pois[1].poi_miles_from_property',
        'neighborhood_pois[2].poi_miles_from_property',
        'neighborhood_pois[3].poi_miles_from_property',
        'neighborhood_pois[4].poi_miles_from_property',
        'neighborhood_pois[5].poi_miles_from_property',
      ],
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 3,
    fieldMappings: {
      poi_name: 'poi_name',
      _poi_walking_minutes: 'poi_walking_minutes',
      _poi_biking_minutes: 'poi_biking_minutes',
      _poi_driving_minutes: 'poi_driving_minutes',
      _poi_miles_from_property: 'poi_miles_from_property',
    },
  },
};

/** Human-readable proximity label, preferring walking distance. */
function proximityLabel(poi: NeighborhoodPoiSlot): string {
  const walk = poi._poi_walking_minutes?.trim();
  if (walk) return `${walk} min walk to`;
  const bike = poi._poi_biking_minutes?.trim();
  if (bike) return `${bike} min bike to`;
  const drive = poi._poi_driving_minutes?.trim();
  if (drive) return `${drive} min drive to`;
  const miles = poi._poi_miles_from_property?.trim();
  if (miles) return `${miles} mi from us`;
  return 'Nearby';
}

// ============================================================================
// Basic street map (Leaflet + free CartoDB "Positron" tiles — no API key)
// ============================================================================

const MAP_PIN_HTML = `
<svg viewBox="0 0 24 32" width="34" height="44" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M12 0C5.9 0 1 4.9 1 11c0 7.7 9.6 19.4 10 19.9.3.4.9.4 1.2 0C12.4 30.4 22 18.7 22 11 22 4.9 17.1 0 12 0z" fill="var(--vibe-color-secondary, #2563eb)"/>
  <circle cx="12" cy="11" r="4.3" fill="#ffffff"/>
</svg>`;

/** Flat, interactive street map centered on the property address. */
function NeighborhoodMapView() {
  const property = useProperty();
  const lat = parseFloat(property?.property_address_latitude ?? '');
  const lng = parseFloat(property?.property_address_longitude ?? '');
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasCoords || !containerRef.current) return;

    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let map: any;

    (async () => {
      const leaflet = await import('leaflet');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const L: any = (leaflet as any).default ?? leaflet;
      if (cancelled || !containerRef.current) return;

      map = L.map(containerRef.current, {
        center: [lat, lng],
        zoom: 14,
        scrollWheelZoom: false,
      });

      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19,
      }).addTo(map);

      const icon = L.divIcon({
        className: 'neighborhood-map__pin',
        html: MAP_PIN_HTML,
        iconSize: [34, 44],
        iconAnchor: [17, 44],
      });
      L.marker([lat, lng], { icon, keyboard: false }).addTo(map);
    })();

    return () => {
      cancelled = true;
      if (map) map.remove();
    };
  }, [hasCoords, lat, lng]);

  if (!hasCoords) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center bg-accent px-6 text-center text-muted-foreground">
        Map unavailable — add the property address latitude and longitude.
      </div>
    );
  }

  return <div ref={containerRef} className="h-full w-full" aria-label="Neighborhood map" />;
}

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const poi1 = useSlotGroup('neighborhoodsection.poi_1', poi1Def) as NeighborhoodPoiSlot;
  const poi2 = useSlotGroup('neighborhoodsection.poi_2', poi2Def) as NeighborhoodPoiSlot;
  const poi3 = useSlotGroup('neighborhoodsection.poi_3', poi3Def) as NeighborhoodPoiSlot;
  const poi4 = useSlotGroup('neighborhoodsection.poi_4', poi4Def) as NeighborhoodPoiSlot;

  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);

  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full overflow-hidden bg-background py-10 md:py-14 lg:py-16"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-10 px-4 text-center sm:px-6 lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start lg:gap-12 lg:px-8 lg:text-left">
        {/* Left: copy, CTA, and walk-to list */}
        <div className="flex w-full flex-col items-center lg:items-start">
          <RichTextContent
            path="neighborhoodsection.heading"
            label="Heading"
            defaultValue="<p>Perfect location</p>"
            as="h2"
            className="font-heading mb-6 text-4xl font-bold leading-[1.05] text-primary sm:text-5xl"
          />

          <RichTextContent
            path="neighborhoodsection.body"
            label="Body Text"
            as="div"
            className="font-body mb-8 max-w-xl text-base leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline"
          />

          <EditableSlotGroup
            groupPath="neighborhoodsection.cta_link"
            definition={ctaLinkDef}
            className="inline-block"
          >
            <a
              href={cta.url || '/neighborhood'}
              target={cta._target || '_self'}
              className="site-cta site-cta--secondary neighborhood-cta"
              style={{
                // Swap the default and hover color pairs for this CTA:
                // base state uses the primary accent, hover flips to secondary.
                ['--site-cta-secondary-accent' as string]: 'var(--vibe-color-primary)',
                ['--site-cta-secondary-on-accent' as string]:
                  'var(--vibe-color-text-on-primary, #ffffff)',
                ['--site-cta-hover-accent' as string]: 'var(--vibe-color-secondary)',
                ['--site-cta-hover-on-accent' as string]:
                  'var(--vibe-color-text-on-secondary, #ffffff)',
              }}
            >
              <RichTextContent
                path="neighborhoodsection.cta_label"
                label="CTA Label"
                defaultValue="<p>Explore Neighborhood</p>"
                as="span"
              />
            </a>
          </EditableSlotGroup>

          <div
            className="mt-10 flex w-full flex-col gap-6 lg:items-start"
            role="list"
            aria-label="Nearby points of interest"
          >
            <EditableSlotGroup
              groupPath="neighborhoodsection.poi_1"
              definition={poi1Def}
              className="flex flex-col"
            >
              <span className="font-body text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {proximityLabel(poi1)}
              </span>
              <span className="font-heading text-xl font-bold leading-tight text-foreground sm:text-2xl">
                {poi1.poi_name || 'Add a nearby point of interest'}
              </span>
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="neighborhoodsection.poi_2"
              definition={poi2Def}
              className="flex flex-col"
            >
              <span className="font-body text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {proximityLabel(poi2)}
              </span>
              <span className="font-heading text-xl font-bold leading-tight text-foreground sm:text-2xl">
                {poi2.poi_name || 'Add a nearby point of interest'}
              </span>
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="neighborhoodsection.poi_3"
              definition={poi3Def}
              className="flex flex-col"
            >
              <span className="font-body text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {proximityLabel(poi3)}
              </span>
              <span className="font-heading text-xl font-bold leading-tight text-foreground sm:text-2xl">
                {poi3.poi_name || 'Add a nearby point of interest'}
              </span>
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="neighborhoodsection.poi_4"
              definition={poi4Def}
              className="flex flex-col"
            >
              <span className="font-body text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {proximityLabel(poi4)}
              </span>
              <span className="font-heading text-xl font-bold leading-tight text-foreground sm:text-2xl">
                {poi4.poi_name || 'Add a nearby point of interest'}
              </span>
            </EditableSlotGroup>
          </div>
        </div>

        {/* Right: basic interactive street map centered on the property */}
        <div className="neighborhood-map relative z-0 h-[440px] w-full overflow-hidden rounded-lg border border-secondary/30 lg:h-[600px]">
          <NeighborhoodMapView />
        </div>
      </div>

      <style>{`
        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        .neighborhood-map .leaflet-container {
          height: 100%;
          width: 100%;
          background: #eaeaea;
          font-family: inherit;
        }
        .neighborhood-map__pin {
          background: transparent;
          border: none;
          line-height: 0;
          filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3));
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Perfect location</p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore Neighborhood</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
    'neighborhoodsection.poi_1': poi1Def,
    'neighborhoodsection.poi_2': poi2Def,
    'neighborhoodsection.poi_3': poi3Def,
    'neighborhoodsection.poi_4': poi4Def,
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
